// src/pages/POSPage.jsx - 完整版本（带确认对话框）
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { productApi, saleApi, shiftApi, categoryApi } from '../api';
import ProductGrid from '../components/pos/ProductGrid';
import ShoppingCart from '../components/pos/ShoppingCart';
import CheckoutConfirmDialog from '../components/pos/CheckoutConfirmDialog';

const POSPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];
  const { onShowReceipt } = useOutletContext() || {};
  
  // 状态管理
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [currentShift, setCurrentShift] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false); // 新增

  // 加载商品、类型和班次
  useEffect(() => {
    loadProducts();
    loadCategories();
    loadCurrentShift();
  }, []);

  // 加载商品列表
  const loadProducts = async () => {
    try {
      const res = await productApi.getList({ status: 'active' });
      if (res.success) {
        setProducts(res.data || []);
        console.log('✅ 加载商品:', res.data.length);
      }
    } catch (err) {
      console.error('❌ 加载商品失败:', err);
      alert(t.loadProductsFailed || '加载商品失败: ' + err.message);
    }
  };

  // 加载商品类型
  const loadCategories = async () => {
    try {
      const res = await categoryApi.getList();
      if (res.success) {
        setCategories(res.data || []);
        console.log('✅ 加载商品类型:', res.data.length);
      }
    } catch (err) {
      console.error('❌ 加载商品类型失败:', err);
    }
  };

  // 加载当前班次
  const loadCurrentShift = async () => {
    try {
      const res = await shiftApi.getCurrent();
      if (res.success && res.data) {
        setCurrentShift(res.data);
        console.log('✅ 当前班次:', res.data);
      } else {
        console.log('⚠️ 没有活动班次');
      }
    } catch (err) {
      console.error('❌ 加载班次失败:', err);
    }
  };

  // 添加商品到购物车
  const handleAddToCart = (product) => {
    const existingItem = cartItems.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCartItems(cartItems.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCartItems([...cartItems, {
        product_id: product.id,
        product_name: product.name,
        product_name_en: product.name_en,
        product_name_km: product.name_km,
        unit_price: parseFloat(product.sale_price),
        quantity: 1,
        barcode: product.barcode
      }]);
    }

    console.log('➕ 添加到购物车:', product.name);
  };

  // 更新商品数量
  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }

    setCartItems(cartItems.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  // 从购物车移除商品
  const handleRemoveFromCart = (productId) => {
    setCartItems(cartItems.filter(item => item.product_id !== productId));
    console.log('🗑️ 移除商品:', productId);
  };

  // 点击结算按钮 - 显示确认对话框
  const handleCheckoutClick = () => {
    if (cartItems.length === 0) {
      alert(t.cartIsEmpty || '购物车是空的！');
      return;
    }

    if (!currentShift) {
      alert(t.pleaseStartShift || '请先开始班次！');
      return;
    }

    // 显示确认对话框
    setShowConfirmDialog(true);
  };

  // 确认支付 - 创建订单
  const handleConfirmPayment = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      // 计算总金额
      const totalAmount = cartItems.reduce((sum, item) => 
        sum + (parseFloat(item.unit_price) * parseInt(item.quantity)), 0
      );

      // 计算商品总数
      const totalItems = cartItems.reduce((sum, item) => 
        sum + parseInt(item.quantity), 0
      );

      // 构建完整的订单数据
      const orderData = {
        shift_id: parseInt(currentShift.id),
        payment_method: paymentMethod,
        total_amount: parseFloat(totalAmount.toFixed(2)),
        total_items: totalItems,
        cashier_id: currentUser?.id,
        cashier_name: currentUser?.username,
        items: cartItems.map(item => ({
          product_id: parseInt(item.product_id),
          product_name: item.product_name,
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          subtotal: parseFloat((item.unit_price * item.quantity).toFixed(2))
        })),
        sale_time: new Date().toISOString()
      };

      console.log('💰 提交订单数据:', JSON.stringify(orderData, null, 2));

      const res = await saleApi.create(orderData);

      console.log('📥 服务器响应:', res);

      if (res.success) {
        console.log('✅ 订单创建成功:', res.data);
        
        // 显示小票（不显示额外的成功提示）
        if (onShowReceipt) {
          onShowReceipt(res.data);
        }

        // 清空购物车
        setCartItems([]);
        
        // 重新加载数据
        await loadProducts();
        await loadCurrentShift();
        
        // 移除了 alert 成功提示
      } else {
        console.error('❌ 服务器返回失败:', res);
        alert(res.message || t.orderFailed || '订单创建失败');
      }
    } catch (err) {
      console.error('❌ 创建订单失败:', err);
      
      if (err.response) {
        console.error('错误状态码:', err.response.status);
        console.error('错误数据:', err.response.data);
        
        const errorMessage = err.response.data?.message || 
                            err.response.data?.error || 
                            JSON.stringify(err.response.data);
        
        alert(`${t.orderFailed || '创建订单失败'}：${errorMessage}`);
      } else if (err.request) {
        console.error('请求已发出但无响应:', err.request);
        alert(
          language === 'en' ? 'Network error, please check connection' :
          language === 'km' ? 'បញ្ហាបណ្តាញ សូមពិនិត្យការតភ្ជាប់' :
          '网络错误，请检查连接'
        );
      } else {
        console.error('错误:', err.message);
        alert(err.message || t.orderFailed || '创建订单失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  // 取消支付
  const handleCancelPayment = () => {
    setShowConfirmDialog(false);
  };

  // 获取类型显示名称
  const getCategoryName = (category) => {
    if (!category) return '';
    switch (language) {
      case 'en':
        return category.name_en || category.name;
      case 'km':
        return category.name_km || category.name;
      default:
        return category.name;
    }
  };

  // 过滤商品（搜索 + 类型）
  const filteredProducts = products.filter(product => {
    if (selectedCategory !== null && product.category_id !== selectedCategory) {
      return false;
    }

    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      product.name?.toLowerCase().includes(keyword) ||
      product.name_en?.toLowerCase().includes(keyword) ||
      product.name_km?.toLowerCase().includes(keyword) ||
      product.barcode?.includes(keyword)
    );
  });

  return (
    <div className="h-full flex flex-col lg:flex-row gap-6">
      {/* 左侧: 商品列表 */}
      <div className="flex-1 lg:w-2/3 min-h-0">
        <div className="bg-white rounded-xl shadow-lg p-6 h-full flex flex-col">
          {/* 顶部固定区域 */}
          <div className="flex-shrink-0 space-y-4 mb-4">
            <h2 className="text-2xl font-bold">{t.pos}</h2>
            
            {/* 搜索框 */}
            <input
              type="text"
              placeholder={t.searchPlaceholder || '搜索商品名称或条码...'}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />

            {/* 商品类型选择器 */}
            <div className="overflow-x-auto">
              <div className="flex gap-2 pb-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    selectedCategory === null
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {language === 'en' ? 'All' : language === 'km' ? 'ទាំងអស់' : '全部'}
                </button>

                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {getCategoryName(category)}
                  </button>
                ))}
              </div>
            </div>

            {/* 班次信息 */}
            {currentShift ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  {t.currentShift || '当前班次'} #{currentShift.id} | {t.startingCash || '起始现金'}: ¥{currentShift.starting_cash}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  ⚠️ {t.noActiveShift || '请先在"班次管理"页面开始班次'}
                </p>
              </div>
            )}
          </div>

          {/* 商品网格 - 可滚动区域 */}
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            <ProductGrid 
              products={filteredProducts}
              onAddToCart={handleAddToCart}
            />
          </div>
        </div>
      </div>

      {/* 右侧: 购物车 */}
      <div className="lg:w-1/3 min-h-0">
        <ShoppingCart
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={handleCheckoutClick}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
        />
      </div>

      {/* 确认支付对话框 */}
      {showConfirmDialog && (
        <CheckoutConfirmDialog
          cartItems={cartItems}
          paymentMethod={paymentMethod}
          onConfirm={handleConfirmPayment}
          onCancel={handleCancelPayment}
        />
      )}
    </div>
  );
};

export default POSPage;